import React, { useState, useMemo, useEffect } from 'react';
import { Clock, Star, Flame, Heart, Shuffle, Rocket } from 'lucide-react';
import RecipeModal from '../cookbook/RecipeModal';
import { DayPlan, Recipe, UserPreferences, useStore } from '../../store/useStore';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getWeekInfo } from '../../lib/weekHelper';
import { formatDate } from '@/lib/dateFormatter';
import TemplateBuilderModal from '../mealplanner/TemplateBuilderModal';
import { useNavigate } from 'react-router-dom';

interface Meal {
  day: string;
  title: string;
  time: string;
  type: string;
  calories: number;
  protein?: number;
  difficulty?: string;
  cookTime?: number;
  recipe?: any;
  recipeId?: string;
}

const DashboardOverview = () => {
  const [viewAll, setViewAll] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const recipes = useStore((state) => state.recipes || []);
  const fetchRecipeById = useStore((state) => state.fetchRecipeById);
  const mealTimes = useStore((state) => state.userPreferences?.mealTimes) || [
    {
      time: "07:00",
      type: "breakfast"
    },
    {
      time: "12:00",
      type: "lunch"
    },
    {
      type: "dinner",
      time: "18:00"
    }
  ];
  const user = useStore((state) => state.user);
  const firstName = useStore((state) => state.user?.firstName || 'Darling');
  const generateWeeklyPlan = useStore((state) => state.generateWeeklyPlan);
  const allPlans = useStore((state) => state.weeklyPlan);
  const userPreferences = useStore((state) => state.userPreferences);
  const addNotification = useStore((state) => state.addNotification);
  const [recipeOfWeekCount, setRecipeOfWeekCount] = useState(0);
  const generatingWeeklyPlan = useStore((state) => state.generatingWeeklyPlan);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
  const mealTemplate = useStore((state) => state.mealTemplate);
  const [mealPlan, setMealPlan] = useState<Recipe[]>([]);
  const [selectedMeals, setSelectedMeals] = useState<Meal[]>([]);
  const navigate = useNavigate();

  const [upcomingMeals, setUpcomingMeals] = useState<Meal[]>([]);
  const [recipeOfTheWeek, setRecipeOfTheWeek] = useState<any>({
    id: 'recipe-of-week',
    title: 'Grilled Salmon with Asparagus',
    image: 'https://images.pexels.com/photos/3655916/pexels-photo-3655916.jpeg',
    prepTime: '10',
    cookTime: '15',
    servings: 2,
    difficulty: 'Medium' as const,
    category: ['Main Course'],
    isFavorite: false,
    ingredients: [
      '2 salmon fillets',
      '1 bunch asparagus',
      '2 tbsp olive oil',
      '1 lemon',
      'Salt and pepper to taste',
      '2 cloves garlic, minced'
    ],
    instructions: [
      'Preheat grill to medium-high heat',
      'Season salmon with olive oil, garlic, salt, and pepper',
      'Trim asparagus and toss with olive oil',
      'Grill salmon for 4-5 minutes per side',
      'Grill asparagus for 5-7 minutes',
      'Serve with lemon wedges'
    ],
    notes: 'For best results, use fresh wild-caught salmon. The asparagus should be bright green and crisp-tender.'
  });

  const [weeklyPlan, setWeeklyPlan] = useState<DayPlan[]>(() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date?.getDate() - date?.getDay() + i);
      return {
        date,
        meals: {
          breakfast: undefined,
          lunch: undefined,
          dinner: undefined,
          snacks: []
        }
      };
    });
    return days;
  });

  const [newWeekMeals, setNewWeekMeals] = useState<Meal[]>([]);

  const openRecipe = async (recipeId: string) => {
    // console.log("Opening recipe: ", recipeId);
    let recipe = await fetchRecipeById(recipeId);
    setSelectedRecipe(recipe);
  }

  useEffect(() => {
    const loadUserPreferences = async () => {
      if (user?.id) {
        setRecipeOfTheWeek(recipes[0]);
        console.log("Working with: ",recipes[0]);
        setRecipeOfWeekCount(0);
        try {
          const userPrefRef = doc(db, 'userPreferences', user.id);
          const userPrefSnap = await getDoc(userPrefRef);
          const userPrefData = {
            theme: 'dark',
            measurementSystem: 'metric',
            notifications: {
              email: true,
              newRecipes: true,
              specialOffers: false,
              weeklyDigest: true
            },
            ...userPrefSnap.data(),
          } as UserPreferences;
          useStore.setState({ userPreferences: userPrefData });
        } catch (error) {
          console.error('Error loading user preferences:', error);
          addNotification({
            title: 'Preferences Load Failed',
            message: 'Unable to load your preferences. Please try again later.',
            type: 'error'
          });
        }
      }
    };

    loadUserPreferences();
  }, [user?.id, addNotification, recipes]);

  useEffect(() => {
    const setFavorite = async () => {
      const weekId = getWeekInfo();
      const allWeekPlans = allPlans;
      const weekPlan = allWeekPlans?.[weekId];
      // console.log("This weeks id is: ", weekId);
      if (weekPlan) {
        const formattedWeekPlan = weekPlan
          .map((item: DayPlan) => ({
            ...item,
            date: formatDate(item.date),
          }))
          .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
        setWeeklyPlan(formattedWeekPlan);
        const mealCount: Record<string, { meal: Meal; count: number }> = {};

        const countMealOccurrences = (mealData: any): Record<string, { meal: Meal; count: number }> => {
          if (!mealData) return mealCount;

          if (Array.isArray(mealData)) {
            mealData.forEach((meal) => {
              const mealTitle = meal.title;
              if (mealCount[mealTitle]) {
                mealCount[mealTitle].count += 1;
              } else {
                mealCount[mealTitle] = { meal, count: 1 };
              }
            });
          } else {
            const mealTitle = mealData.title;
            mealCount[mealTitle] = { meal: mealData, count: 1 };
          }
          return mealCount;
        };

        const processMealCounts = (formattedWeekPlan: DayPlan[]) => {
          let mealOccurrences: Record<string, { meal: Meal; count: number }> = {};

          formattedWeekPlan.forEach((item: DayPlan) => {
            const { breakfast, lunch, dinner } = item.meals;

            const breakfastCount = countMealOccurrences(breakfast);
            const lunchCount = countMealOccurrences(lunch);
            const dinnerCount = countMealOccurrences(dinner);

            mealOccurrences = {
              ...mealOccurrences,
              ...breakfastCount,
              ...lunchCount,
              ...dinnerCount,
            };
          });

          return mealOccurrences;
        };

        const mealOccurrences = processMealCounts(formattedWeekPlan);

        const mostFrequentMeal = Object.entries(mealOccurrences).reduce(
          (prev: [string, { meal: Meal; count: number }], current: [string, { meal: Meal; count: number }]) =>
            (current[1].count > prev[1].count ? current : prev),
          ['', { meal: { day: '', title: '', time: '', type: '', calories: 0 }, count: 0 }]
        );

        let favoriteRecipe: Recipe | undefined = undefined;
        const recipeId = mostFrequentMeal[1].meal.recipeId;
        if (recipeId) {
          favoriteRecipe = await fetchRecipeById(String(recipeId));
        }

        // console.log("Most frequent meal: ", mostFrequentMeal);
        // console.log("Favorite recipe: ", favoriteRecipe, recipeId);

        if (favoriteRecipe) {
          setRecipeOfTheWeek(favoriteRecipe);
          setRecipeOfWeekCount(mostFrequentMeal[1].count);
        }
      }
    };
    setFavorite();
  }, [allPlans, recipes]);

  useEffect(() => {
    if (!newWeekMeals || newWeekMeals.length === 0) return;
    const combinedMeals = [...upcomingMeals, ...newWeekMeals];
    setUpcomingMeals(combinedMeals);
  }, [newWeekMeals]);

  useEffect(() => {
    const weekId = getWeekInfo();
    const weekPlan = allPlans?.[weekId];
    console.log('Week Plan:', weekPlan);
    if (weekPlan) {
      const formattedWeekPlan = weekPlan
        .map((item: DayPlan) => {
          return (
            {
              ...item,
              date: formatDate(item.date),
            }
          )
        })
        .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
      setWeeklyPlan(formattedWeekPlan);

      const extractMealsFromType = (
        mealData: any,
        type: 'Breakfast' | 'Lunch' | 'Dinner',
        day: string
      ) => {
        if (!mealData) return [];
        if (Array.isArray(mealData)) {
          return mealData.map((meal) => {
            const recipe = fetchRecipeById(meal.recipeId);
            return {
              day,
              title: meal.title,
              time: meal.time ?? mealTimes.find(meal => meal.type === type.toLowerCase())?.time,
              type,
              calories: meal.calories || 0,
              cookTime: recipe?.cookTime,
              recipe
            };
          });
        } else {
          const recipe = fetchRecipeById(mealData.recipeId);
          return [
            {
              day,
              title: mealData.title,
              time: mealData.time ?? mealTimes.find(meal => meal.type === type.toLowerCase())?.time,
              type,
              calories: mealData.calories || 0,
              cookTime: recipe.cookTime,
              recipe
            },
          ];
        }
      };

      const extractedMeals = formattedWeekPlan.flatMap((item: DayPlan) => {
        // console.log(item.date);
        const day = item.date?.toLocaleDateString('en-US', { weekday: 'short' });
        const { breakfast, lunch, dinner } = item.meals;

        return [
          ...extractMealsFromType(breakfast, 'Breakfast', day),
          ...extractMealsFromType(lunch, 'Lunch', day),
          ...extractMealsFromType(dinner, 'Dinner', day),
        ];
      });

      const getUpcomingMeals = (meals: Meal[]): Meal[] => {
        const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

        const upcomingMeals = meals.filter((meal) => {
          const hasPassed = isTimePassed(meal.day, meal.time);
          return !hasPassed;
        });

        const sortedMeals = upcomingMeals.sort((a, b) => {
          return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
        });

        return sortedMeals;
      };

      let upcomingMealsTmp = getUpcomingMeals(extractedMeals);
      let today = new Date();
      if (today.getDay() == 0) {
        let [week, year] = weekId.split("_");
        const newWeekId = `${Number(week) + 1}_${year}`
        const newWeek = allPlans?.[newWeekId];
        console.log("New Week: ", newWeekId, newWeek)
        if (newWeek) {
          const formattedNewWeekPlan = newWeek
            .map((item: DayPlan) => ({
              ...item,
              date: formatDate(item.date),
            }))
            .sort((a: any, b: any) => a.date.getTime() - b.date.getTime());
          const extractedMeals = formattedNewWeekPlan.flatMap((item: DayPlan) => {
            const day = item.date.toLocaleDateString('en-US', { weekday: 'short' });
            const { breakfast, lunch, dinner } = item.meals;

            return [
              ...extractMealsFromType(breakfast, 'Breakfast', day),
              ...extractMealsFromType(lunch, 'Lunch', day),
              ...extractMealsFromType(dinner, 'Dinner', day),
            ];
          });
          // console.log("Extracted New Week: ", extractedMeals, formattedNewWeekPlan)
          setNewWeekMeals(extractedMeals);
        }
      }
      let upcomingMeals = [...upcomingMealsTmp, ...newWeekMeals];
      // console.log("upcoming meals: ", weekPlan, extractedMeals, upcomingMeals, newWeekMeals, today.getDay());

      setUpcomingMeals(upcomingMeals);
    }
  }, [allPlans]);

  function isTimePassed(dayStr: string, timeStr: string): boolean {
    const [hour, minute] = timeStr.split(":").map(Number);
    const now = new Date();

    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const targetDay = new Date();
    const dayIndex = daysOfWeek.indexOf(dayStr);

    // console.log(targetDay, targetDay.getDay(), dayIndex, dayStr, timeStr)

    if (dayIndex === -1 || isNaN(hour) || isNaN(minute)) return true;

    // const currentDayIndex = targetDay.getDay();
    const currentJsDay = targetDay.getDay(); // 0 (Sun) - 6 (Sat)
    const currentDayIndex = (currentJsDay + 6) % 7; // Now 0 (Mon) - 6 (Sun)
    const offset = dayIndex - currentDayIndex;
    targetDay.setDate(targetDay.getDate() + offset);
    targetDay.setHours(hour, minute, 0, 0);
    return now > targetDay;
  }

  const stats = useMemo(() => {
    const dailyCalories = weeklyPlan.map(day => {
      let total = 0;
      let breakfast = Array.isArray(day.meals.breakfast) ? day.meals.breakfast : [day.meals.breakfast];
      let lunch = Array.isArray(day.meals.lunch) ? day.meals.lunch : [day.meals.lunch];
      let dinner = Array.isArray(day.meals.dinner) ? day.meals.dinner : [day.meals.dinner];
      if (breakfast) {
        total += breakfast.reduce((sum, meal: any) => sum + (meal?.calories || 0), 0);
      }
      if (lunch) {
        total += lunch.reduce((sum, meal: any) => sum + (meal?.calories || 0), 0);
      }
      if (dinner) {
        total += dinner.reduce((sum, meal: any) => sum + (meal?.calories || 0), 0);
      }
      return total;
    });

    const avgCalories = dailyCalories.length > 0
      ? Math.round(dailyCalories.reduce((a, b) => a + b, 0) / dailyCalories.length)
      : 0;

    const favoriteRecipesCount = recipes.filter(recipe => recipe.isFavorite).length;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    let nextMeal;
    // console.log("Upcoming Meal: ", upcomingMeals[0]);
    if (upcomingMeals[0]) {
      const nextMealTmp = [upcomingMeals[0]]
        .map(mt => {
          const [hours, minutes] = mt?.time?.split(':').map(Number);
          return {
            type: mealTimes.find(item => item.time === mt.time)?.type || "breakfast",
            time: mt.time,
            minutesFromMidnight: hours * 60 + minutes,
            formattedTime: new Date(now.setHours(hours, minutes)).toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit'
            })
          };
        })
        .find(mt => mt.minutesFromMidnight > currentTime) || mealTimes[0];
      nextMeal = nextMealTmp;
    }

    // console.log(nextMeal, upcomingMeals[0])

    return {
      avgCalories,
      favoriteRecipesCount,
      nextMeal
    };
  }, [weeklyPlan, recipes, mealTimes, upcomingMeals]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Hello {firstName}!</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Star className="text-yellow-500" />}
          title={recipeOfTheWeek?.title}
          value={`${recipeOfWeekCount}x`}
          trend="Times planned (this week)"
        />
        <StatCard
          icon={<Clock className="text-blue-500" />}
          title="Next Meal (This Week)"
          value={stats.nextMeal ? stats.nextMeal?.time : 'No meals scheduled'}
          trend={stats.nextMeal ? `${stats.nextMeal.type.charAt(0).toUpperCase() + stats.nextMeal.type.slice(1)}` : 'Set up meal times'}
        />
        <StatCard
          icon={<Flame className="text-orange-500" />}
          title="Weekly Calories"
          value={stats.avgCalories}
          trend="Daily average"
        />
        <StatCard
          icon={<Heart className="text-red-500" />}
          title="Favorite Recipes"
          value={stats.favoriteRecipesCount}
          trend="Marked as favorite"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 data-tooltip="upcoming-meals" className="font-semibold text-gray-800 dark:text-white">Upcoming Meals (This Week)</h2>
            <button
              onClick={() => setViewAll(!viewAll)}
              className="text-sm text-secondary hover:text-secondary/80 font-medium"
            >
              {viewAll ? 'Show Less' : 'Show All'}
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto max-h-[400px]">
            {((!viewAll ? upcomingMeals.slice(0, 5) : upcomingMeals).length === 0) ? (
              <div className="flex flex-col items-center justify-center h-64 text-center text-gray-500 dark:text-gray-400">
                <svg className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h1l3.6 7.59a1 1 0 00.9.41H19a1 1 0 00.9-.55l3-6A1 1 0 0022 10H6.42l-.95-2H3"></path>
                </svg>
                <p className="text-sm">No upcoming meals scheduled.</p>
                <p className="text-xs mt-1">Check back later or add a new meal plan.</p>
                <button
                  // onClick={() => setShowTemplateModal(true)}
                  onClick={() => navigate("/meal-planner")}
                  // disabled={isGeneratingPlan}
                  data-tooltip="create-plan"
                  className="flex items-center px-4 mt-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-700/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Rocket size={18} className="mr-2" />
                  {isGeneratingPlan ? 'Generating Plan...' : 'Add Meals'}
                </button>
              </div>
            ) : (
              (!viewAll ? upcomingMeals.slice(0, 5) : upcomingMeals).map((meal, index) => (
                <div
                  key={index}
                  onClick={() => {
                    console.log("Clicked!", meal);
                    if (meal?.recipe) {
                      setSelectedRecipe(meal.recipe)
                      // openRecipe(meal.recipeId);
                    }
                  }}
                  className="flex items-center cursor-pointer p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                >
                  <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center mr-4">
                    <span className="text-secondary font-medium">{meal.day}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800 dark:text-white">{meal.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{meal.time} · {meal.type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-800 dark:text-white">{meal.calories} kcal</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{meal.cookTime}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-800 dark:text-white">Recipe of the Week</h2>
          </div>
          <div className="p-6">
            <div className="rounded-lg overflow-hidden mb-4 relative">
              <img
                src={recipeOfTheWeek?.image}
                alt={recipeOfTheWeek?.title}
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-4 text-white">
                  <h3 className="font-bold text-xl">{recipeOfTheWeek?.title}</h3>
                  <p className="text-sm opacity-90">25 min · Medium · {recipeOfTheWeek?.category?.[0]}</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Calories</span>
                <span className="font-medium text-gray-800 dark:text-white">{recipeOfTheWeek?.calories} cal</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Estimated Cost</span>
                <span className="font-medium text-gray-800 dark:text-white">
                  £{
                    (() => {
                      // safest way: getAmount returns a valid number or 0
                      const getAmount = (val: any) => {
                        const n = Number(val);
                        return isNaN(n) ? 0 : n;
                      };

                      if(!user || !recipeOfTheWeek) return '0.00';

                      const userCost = user?.estimatedCosts?.[recipeOfTheWeek?.id]?.amount;
                      const recipeUserCost = recipeOfTheWeek?.estimatedCosts?.[user?.id]?.amount;
                      const recipeCost = recipeOfTheWeek?.estimatedCost;

                      // pick first non-null numeric value
                      const amount = userCost ?? recipeUserCost ?? recipeCost ?? 0;

                      return getAmount(amount).toFixed(2);
                    })()
                  }
                </span>

              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Times Cooked</span>
                <span className="font-medium text-gray-800 dark:text-white">{recipeOfWeekCount}x this week</span>
              </div>
              <div className="pt-3">
                <button
                  onClick={() => setSelectedRecipe(recipeOfTheWeek)}
                  className="w-full py-2 px-4 bg-secondary-700 hover:bg-secondary/90 text-white rounded-lg transition-colors duration-200"
                >
                  View Recipe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedRecipe && (
        <RecipeModal
          recipe={selectedRecipe}
          onClose={() => setSelectedRecipe(null)}
        />
      )}
      <TemplateBuilderModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        template={mealTemplate}
        onUpdateTemplate={setMealPlan}
      />
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  trend: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, trend }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
      <div
        {...(title === "Weekly Calories" ? { "data-tooltip": "weekly-calories" } : title === "Next Meal (This Week)" ? { "data-tooltip": "next-meal" } : {})}
        className="flex justify-between items-start"
      >
        <div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-full bg-gray-100 p-2 dark:bg-gray-700 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">{trend}</p>
    </div>
  );
};

export default DashboardOverview;